import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { Button } from '../../components/Button/Button';
import { BaseLayout } from '../../components/Layout/Layout';
import { useLanguage } from '../../locale/useLanguage';

import './CompleteUserInfoView.css';
import { useHistory } from 'react-router';
import { Routes } from '../../routes/types';
import {
  useGetGeoInfoMutation,
  useGetProfileQuery,
  useUpdateProfileMutation,
} from '../../apis/mlkApi/mlk.api.hooks';
import {
  CompleteBusinessProfileForm,
  CompleteProfileForm,
  useCompleteUserInfoBusinessValidationForm,
  useCompleteUserInfoPrivateValidationForm,
} from '../../hooks/validations';
import { parseRes } from '../../apis/utils';
import { useSelector } from 'react-redux';
import { getOperationSelector } from '../../redux/general/general.selectors';
import { PrivateForm } from './components/PrivateForm/PrivateForm';
import {
  RadioButtonGroup,
  RadioButtonGroupOption,
} from '../../components/RadioButtonGroup/RadioButtonGroup';
import { UserType } from '../../models/user.models';
import { BusinessForm } from './components/BusinessForm/BusinessForm';
import { GetGeoInfoParams } from '../../models/address.models';

type CompleteUserInfoProps = {};

export const CompleteUserInfo: React.FC<CompleteUserInfoProps> = () => {
  const [t] = useLanguage();
  const history = useHistory();
  const [saveUser] = useUpdateProfileMutation();
  const { data: userInfo } = useGetProfileQuery(undefined);

  const operation = useSelector(getOperationSelector);
  const [getGeoInfo] = useGetGeoInfoMutation();

  const [countryList, setCountryList] = useState<Array<string>>();
  const [stateList, setStateList] = useState<Array<string>>();
  const [cityList, setCityList] = useState<Array<string>>();
  const [postalCodeList, setPostalCodeList] = useState<Array<string>>();

  const updateGeoInfo = useCallback(
    async (value: GetGeoInfoParams) => {
      const res = await getGeoInfo(value);
      const { successRes } = parseRes(res);
      if (successRes) {
        const { country, state, city, postalCode } = value;
        if (!country && !state && !city && !postalCode) {
          setCountryList(successRes.data);
        } else if (country && !state && !city && !postalCode) {
          setStateList(successRes?.data);
        } else if (country && state && !city && !postalCode) {
          setCityList(successRes.data);
        } else if (!postalCode) {
          setPostalCodeList(successRes.data);
        }
      }
    },
    [getGeoInfo]
  );

  useEffect(() => {
    updateGeoInfo({ country: '' });
  }, [updateGeoInfo]);

  const [userType, setUserType] = useState<UserType>(UserType.Private);

  const { handleSubmit: handlePrivateSubmit, ...privateValidators } =
    useCompleteUserInfoPrivateValidationForm(userInfo, updateGeoInfo);

  const { handleSubmit: handleBusinessSubmit, ...businessValidators } =
    useCompleteUserInfoBusinessValidationForm(userInfo, updateGeoInfo);

  useEffect(() => {
    if (userInfo) {
      setUserType(userInfo.company ? UserType.Business : UserType.Private);
    }
  }, [userInfo]);

  const userTypeOptions: RadioButtonGroupOption<UserType>[] = useMemo(
    () => [
      {
        label: t('completeUserInfoView.private'),
        value: UserType.Private,
      },
      {
        label: t('completeUserInfoView.business'),
        value: UserType.Business,
      },
    ],
    [t]
  );

  const goBack = useCallback(() => {
    if (history.action === 'POP') {
      history.push(Routes.Home);
    } else {
      history.goBack();
    }
  }, [history]);

  const saveUserInfo = useCallback(
    (data: CompleteProfileForm | CompleteBusinessProfileForm) => {
      saveUser({
        company: UserType.Business === userType,
        ...data,
      }).then((res) => {
        const { successRes, errorRes } = parseRes(res);
        if (successRes.data) {
          if (operation) {
            history.push(operation.routing.url, operation.routing.state);
          } else {
            history.push(Routes.Home);
          }
        }
        if (errorRes) {
          // TODO: Handle error
        }
      });
    },
    [history, operation, saveUser, userType]
  );

  const submit = useMemo(() => {
    return userType === UserType.Private
      ? handlePrivateSubmit(saveUserInfo)
      : handleBusinessSubmit(saveUserInfo);
  }, [handleBusinessSubmit, handlePrivateSubmit, saveUserInfo, userType]);

  return (
    <BaseLayout
      hasBack
      onBackClick={goBack}
      title={t('pageTitles.completeYourProfile')}
    >
      <div className="complete-profile--field-container">
        <label className="complete-profile--label-style ">
          {t('completeUserInfoView.accountType')}
        </label>
        <RadioButtonGroup
          options={userTypeOptions}
          selectedValue={userType}
          onChangeValue={setUserType}
        />
      </div>
      {userType === UserType.Private && (
        <PrivateForm
          validators={privateValidators}
          countries={countryList}
          states={stateList}
          cities={cityList}
          postalCodes={postalCodeList}
        />
      )}
      {userType === UserType.Business && (
        <BusinessForm
          validators={businessValidators}
          countries={countryList}
          states={stateList}
          cities={cityList}
          postalCodes={postalCodeList}
        />
      )}
      <div className="complete-profile--buttons-container">
        <Button
          type="submit"
          onClick={submit}
          isPrimary
          label={t('completeUserInfoView.confirm')}
        />
      </div>
    </BaseLayout>
  );
};
